import React, { useState } from 'react';
import { Alert, Badge } from 'reactstrap';
import ActionButton from '../../../../Components/Common/ActionButton';
import { formatDateTime } from '../../../../utils/format';
import { formatPrice } from '../../utils/formatters';
import {
  activeRounds,
  PARTNER_STATUS,
  SENDER_SIDE,
} from '../../utils/negotiationStatus';
import { copy, isBuyerLocale } from '../../utils/negotiationCopy';

const pairRounds = rounds => {
  const pairs = [];
  activeRounds({ rounds }).forEach(round => {
    const open = pairs[pairs.length - 1];
    if (round.senderSide === SENDER_SIDE.PERTAMINA) {
      pairs.push({ buyer: round, seller: null });
      return;
    }
    if (open && !open.seller) {
      open.seller = round;
      return;
    }
    pairs.push({ buyer: null, seller: round });
  });
  return pairs;
};

const SidePrice = ({ label, price, waiting, align }) => (
  <div className={`neg-timeline__side${align ? ' text-end' : ''}`}>
    <span className='text-muted'>{label}</span>
    <strong className={waiting ? 'text-muted fw-medium' : undefined}>
      {waiting || price}
    </strong>
  </div>
);

const money = (round, unit) =>
  round && round.price != null && round.price !== ''
    ? formatPrice(round.price, unit)
    : null;

const pairText = (pair, unit) => {
  const left = money(pair?.buyer, unit);
  const right = money(pair?.seller, unit);
  if (left && right) return `${left} vs ${right}`;
  return left || right || '';
};

const PairCard = ({ pair, index, unit, done, locale }) => {
  const waitingSeller = locale === 'id' ? 'Menunggu mitra' : 'Waiting seller';
  const waitingBuyer = locale === 'id' ? 'Menunggu Pertamina' : 'Waiting buyer';
  const buyerLabel = locale === 'id' ? 'Target Pertamina' : 'Pertamina target';
  const sellerLabel = locale === 'id' ? 'Counter mitra' : 'Supplier counter';
  const left = money(pair.buyer, unit);
  const right = money(pair.seller, unit);
  const both = Boolean(left && right);
  const pending = !done && !both;
  const when = pair.seller?.createdDate || pair.buyer?.createdDate;
  const solo = left
    ? { label: buyerLabel, price: left }
    : { label: sellerLabel, price: right };

  return (
    <li
      className={`neg-timeline__item${pending ? ' is-pending' : ''}${done ? ' is-done' : ''}`}
    >
      <span className='neg-timeline__dot' aria-hidden='true' />
      <div className='neg-timeline__card'>
        <div className='neg-timeline__meta'>
          <span className='fw-semibold'>Round {index}</span>
          <span className='text-muted small'>{formatDateTime(when)}</span>
        </div>
        {both || pending ? (
          <div className='neg-timeline__prices'>
            <SidePrice
              label={buyerLabel}
              price={left}
              waiting={left ? null : waitingBuyer}
            />
            <span className='neg-timeline__vs'>vs</span>
            <SidePrice
              align
              label={sellerLabel}
              price={right}
              waiting={right ? null : waitingSeller}
            />
          </div>
        ) : (
          <SidePrice label={solo.label} price={solo.price} />
        )}
      </div>
    </li>
  );
};

const NegotiationRoundHistory = ({ line, unit, mode = 'supplier' }) => {
  const [showAll, setShowAll] = useState(false);
  const pairs = pairRounds(line?.rounds || []);
  const newestFirst = [...pairs].reverse();
  const locale = isBuyerLocale(mode) ? 'id' : 'en';
  const isDone = line?.statusLabel === PARTNER_STATUS.ACCEPTED;
  const finalPair = pairs[pairs.length - 1];
  const visible = showAll ? newestFirst : newestFirst.slice(0, 1);
  const hasMore = newestFirst.length > 1;
  const finalText =
    pairText(finalPair, unit) ||
    (line?.acceptedPrice != null ? formatPrice(line.acceptedPrice, unit) : '');

  return (
    <section className='neg-panel mt-3'>
      <header className='neg-panel__head'>
        <div>
          <h3 className='neg-panel__title'>
            <i className='ri-history-line' aria-hidden='true' />
            {locale === 'id' ? 'Riwayat round' : 'Round history'}
          </h3>
          <p className='neg-panel__desc'>
            {locale === 'id'
              ? 'Satu round = target Pertamina vs counter mitra. Terbaru di atas'
              : 'One round pairs the Pertamina target with the supplier counter. Latest on top'}
          </p>
        </div>
        <Badge
          color={isDone ? 'success' : pairs.length ? 'info' : 'secondary'}
          className='px-2 py-1 fs-12 fw-medium rounded-pill'
        >
          {pairs.length === 0
            ? locale === 'id'
              ? 'Belum mulai'
              : 'Not started'
            : `Round ${pairs.length}`}
        </Badge>
      </header>
      <div className='neg-panel__body'>
        {isDone && finalText && (
          <Alert color='success' className='mb-3'>
            <i className='ri-checkbox-circle-line me-2 align-middle' />
            {`Final: ${finalText}`}
          </Alert>
        )}
        {isDone && !finalText && (
          <Alert color='success' className='mb-3'>
            <i className='ri-checkbox-circle-line me-2 align-middle' />
            {copy.historyDoneAlert(
              formatPrice(line.acceptedPrice, unit),
              locale
            )}
          </Alert>
        )}

        {pairs.length === 0 ? (
          <div className='neg-empty'>
            <i className='ri-history-line' aria-hidden='true' />
            <p className='fw-semibold text-dark mb-1'>
              {locale === 'id' ? 'Belum ada round' : 'No rounds yet'}
            </p>
            <p className='mb-0 small'>
              {locale === 'id'
                ? 'Round 1 dimulai saat Pertamina mengirim target.'
                : 'Round 1 starts when Pertamina sends a target.'}
            </p>
          </div>
        ) : (
          <>
            <ul className='neg-timeline'>
              {visible.map(pair => {
                const index = pairs.indexOf(pair) + 1;
                return (
                  <PairCard
                    key={pair.buyer?.id || pair.seller?.id || index}
                    pair={pair}
                    index={index}
                    unit={unit}
                    done={isDone && pair === finalPair}
                    locale={locale}
                  />
                );
              })}
            </ul>
            {hasMore && (
              <div className='mt-2'>
                <ActionButton
                  variant='secondary'
                  type='button'
                  className='w-100'
                  icon={showAll ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}
                  onClick={() => setShowAll(value => !value)}
                >
                  {showAll
                    ? locale === 'id'
                      ? 'Tampilkan round terakhir'
                      : 'Show last round'
                    : locale === 'id'
                      ? `Tampilkan semua (${pairs.length})`
                      : `Show all (${pairs.length})`}
                </ActionButton>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default NegotiationRoundHistory;
